import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { getTimeSlots, formatTime } from '../../utils/timeUtils';

// Helper function to check if a time slot overlaps with an availability range
const isTimeSlotOverlapping = (timeSlot, startTime, endTime) => {
  const timeSlotStart = timeSlot;
  const timeSlotEnd = addMinutesToTime(timeSlot, 30);
  return timeSlotStart < endTime && timeSlotEnd > startTime;
};

const addMinutesToTime = (timeString, minutes) => {
  const [hours, mins] = timeString.split(':').map(Number);
  const totalMinutes = hours * 60 + mins + minutes;
  const newHours = Math.floor(totalMinutes / 60);
  const newMins = totalMinutes % 60;
  return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
};

const TimeSlotCell = ({
  time,
  planningData,
  onSessionDrop,
  userType,
  userId = null,
  isResponsive = false,
}) => {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'session',
      drop: (item) => onSessionDrop(item, planningData.date, time),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [time, planningData.date, onSessionDrop]
  );

  const overlapsForBucket = (matchUserIds) =>
    planningData.availability?.filter(
      (avail) =>
        matchUserIds(avail.user_id) &&
        isTimeSlotOverlapping(time, formatTime(avail.start_time), formatTime(avail.end_time))
    ) || [];

  const tierForOverlaps = (overlaps) => {
    if (overlaps.some((s) => s.availability_type === 'preferred')) return 'preferred';
    if (overlaps.some((s) => s.availability_type === 'available')) return 'available';
    if (overlaps.some((s) => s.availability_type === 'unavailable')) return 'unavailable';
    return 'none';
  };

  const getAvailabilityInfo = () => {
    if (userType === 'specific' && userId != null) {
      const overlaps = overlapsForBucket((uid) => uid === userId);
      const tier = tierForOverlaps(overlaps);
      return {
        tier,
        isAvailable: tier === 'preferred' || tier === 'available',
        isPreferred: tier === 'preferred',
        count: overlaps.length,
      };
    }

    const userIds =
      userType === 'rowers'
        ? planningData.rowers?.map((r) => r.id) || []
        : planningData.coaches?.map((c) => c.id) || [];

    const allOverlaps = overlapsForBucket((uid) => userIds.includes(uid));

    const availableUsers = allOverlaps.filter(
      (avail) => avail.availability_type === 'available' || avail.availability_type === 'preferred'
    );

    const preferredUsers = availableUsers.filter((avail) => avail.availability_type === 'preferred');
    const regularAvailableUsers = availableUsers.filter(
      (avail) => avail.availability_type === 'available'
    );

    const hasUnavailableOverlap = allOverlaps.some((a) => a.availability_type === 'unavailable');

    return {
      tier: tierForOverlaps(allOverlaps),
      isAvailable: availableUsers.length > 0,
      hasPreferred: preferredUsers.length > 0,
      preferredCount: preferredUsers.length,
      availableCount: regularAvailableUsers.length,
      totalAvailable: availableUsers.length,
      totalUsers: userIds.length,
      hasUnavailableOverlap,
    };
  };

  const getTooltipContent = () => {
    if (userType === 'specific' && userId != null) {
      const user = [...(planningData.rowers || []), ...(planningData.coaches || [])].find(
        (u) => u.id === userId
      );
      const overlaps = overlapsForBucket((uid) => uid === userId);
      const tier = tierForOverlaps(overlaps);
      let availabilityText;
      switch (tier) {
        case 'preferred':
          availabilityText = 'Preferred time';
          break;
        case 'available':
          availabilityText = 'Available';
          break;
        case 'unavailable':
          availabilityText = 'Marked unavailable';
          break;
        default:
          availabilityText = 'No availability entered';
      }
      return `${user?.name || 'User'}: ${availabilityText}`;
    }

    const userIds =
      userType === 'rowers'
        ? planningData.rowers?.map((r) => r.id) || []
        : planningData.coaches?.map((c) => c.id) || [];

    const allOverlaps = overlapsForBucket((uid) => userIds.includes(uid));

    const availableUsers = allOverlaps.filter(
      (avail) => avail.availability_type === 'available' || avail.availability_type === 'preferred'
    );

    const hasUnavailableOverlap = allOverlaps.some((a) => a.availability_type === 'unavailable');

    const preferredUsers = availableUsers.filter((avail) => avail.availability_type === 'preferred');
    const regularUsers = availableUsers.filter((avail) => avail.availability_type === 'available');

    if (availableUsers.length === 0) {
      return hasUnavailableOverlap
        ? 'No preferred/available time (some unavailable entries in this slot)'
        : 'No preferred/available overlap for this time';
    }

    let tooltip = '';
    if (preferredUsers.length > 0) {
      const preferredNames = preferredUsers
        .map((avail) => {
          const user = [...(planningData.rowers || []), ...(planningData.coaches || [])].find(
            (u) => u.id === avail.user_id
          );
          return user?.name || 'Unknown';
        })
        .join(', ');
      tooltip += `Preferred: ${preferredNames}`;
    }

    if (regularUsers.length > 0) {
      const regularNames = regularUsers
        .map((avail) => {
          const user = [...(planningData.rowers || []), ...(planningData.coaches || [])].find(
            (u) => u.id === avail.user_id
          );
          return user?.name || 'Unknown';
        })
        .join(', ');
      if (tooltip) tooltip += ' | ';
      tooltip += `Available: ${regularNames}`;
    }

    return tooltip;
  };

  const availInfo = getAvailabilityInfo();

  let cellBgClass = '';
  let textClass = 'text-gray-700';
  let displayContent = '';

  if (userType === 'specific') {
    if (availInfo.tier === 'preferred') {
      cellBgClass = 'bg-emerald-400 hover:bg-emerald-500';
      textClass = 'text-white';
      displayContent = '★';
    } else if (availInfo.tier === 'available') {
      cellBgClass = 'bg-blue-400 hover:bg-blue-500';
      textClass = 'text-white';
      displayContent = '✓';
    } else if (availInfo.tier === 'unavailable') {
      cellBgClass = 'bg-rose-200 hover:bg-rose-300';
      textClass = 'text-rose-900';
      displayContent = '–';
    } else {
      cellBgClass = 'bg-gray-200 hover:bg-gray-300';
      textClass = 'text-gray-500';
      displayContent = '✗';
    }
  } else {
    if (!availInfo.isAvailable) {
      cellBgClass = availInfo.hasUnavailableOverlap
        ? 'bg-stone-300 hover:bg-stone-400'
        : 'bg-gray-200 hover:bg-gray-300';
      textClass = 'text-gray-500';
    } else if (availInfo.hasPreferred && availInfo.preferredCount >= availInfo.availableCount) {
      cellBgClass = 'bg-emerald-400 hover:bg-emerald-500';
      textClass = 'text-white';
    } else if (availInfo.hasPreferred) {
      cellBgClass = 'bg-teal-400 hover:bg-teal-500';
      textClass = 'text-white';
    } else {
      const percentage = (availInfo.totalAvailable / availInfo.totalUsers) * 100;
      if (percentage >= 75) {
        cellBgClass = 'bg-blue-500 hover:bg-blue-600';
        textClass = 'text-white';
      } else if (percentage >= 50) {
        cellBgClass = 'bg-blue-400 hover:bg-blue-500';
        textClass = 'text-white';
      } else {
        cellBgClass = 'bg-blue-300 hover:bg-blue-400';
        textClass = 'text-blue-900';
      }
    }
  }

  if (canDrop && isOver) {
    cellBgClass = `${cellBgClass} ring-2 ring-blue-500 ring-inset shadow-lg`;
  }

  const cellClasses = isResponsive
    ? `border-r border-gray-200 last:border-r-0 p-1 h-12 transition-colors duration-100 ease-in-out flex items-center justify-center relative group cursor-pointer ${cellBgClass}`
    : `border-r border-gray-200 last:border-r-0 p-1 w-16 h-12 transition-colors duration-100 ease-in-out flex items-center justify-center relative group cursor-pointer flex-shrink-0 ${cellBgClass}`;

  return (
    <div
      ref={drop}
      className={cellClasses}
      style={{
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <span className={`text-sm font-bold ${textClass}`}>
        {userType === 'specific' ? displayContent : ''}
      </span>

      <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1 -translate-x-1/2 transform whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
        {getTooltipContent()}
      </div>
    </div>
  );
};

TimeSlotCell.propTypes = {
  time: PropTypes.string.isRequired,
  planningData: PropTypes.object.isRequired,
  onSessionDrop: PropTypes.func.isRequired,
  userType: PropTypes.oneOf(['rowers', 'coaches', 'specific']).isRequired,
  userId: PropTypes.number,
  isResponsive: PropTypes.bool,
};

const DailyAvailabilityGrid = ({ planningData, selectedCrews = [], onSessionDrop }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  if (!planningData) return null;

  const timeSlots = getTimeSlots(6, 21, 30);
  const LABEL_WIDTH = 160;
  const SIDEBAR_WIDTH = 256;
  const PADDING_BUFFER = 32;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const gridCols = timeSlots.length;
  const maxColumnWidth = Math.floor(
    (windowWidth - SIDEBAR_WIDTH - LABEL_WIDTH - PADDING_BUFFER) / gridCols
  );

  const columnWidth = Math.max(32, Math.min(80, maxColumnWidth));
  const gridTemplateColumns = `repeat(${gridCols}, ${columnWidth}px)`;

  const crews = planningData.crews || [];
  const allRowers = planningData.rowers || [];
  const allCoaches = planningData.coaches || [];

  let filteredRowers = allRowers;
  let filteredCoaches = allCoaches;

  if (selectedCrews.length > 0) {
    // Normalize to numbers so crew / training-group matching survives string IDs from URLs or APIs.
    const selectedIds = new Set(selectedCrews.map((id) => Number(id)));
    const selectedCrewObjects = crews.filter((c) => selectedIds.has(Number(c.id)));

    const selectedTrainingGroupIds = new Set(
      selectedCrewObjects
        .map((c) => c.training_group_id)
        .filter((id) => id != null)
        .map((id) => Number(id))
    );

    const selectedRowerIds = new Set();

    selectedCrewObjects.forEach((crew) => {
      (crew.rower_ids || []).forEach((id) => selectedRowerIds.add(Number(id)));
    });

    filteredRowers = allRowers.filter((rower) => selectedRowerIds.has(Number(rower.id)));
    filteredCoaches = allCoaches.filter((coach) => {
      const tgIds = coach.training_group_ids || [];
      const matchesGroup =
        selectedTrainingGroupIds.size > 0 &&
        tgIds.some((tgId) => selectedTrainingGroupIds.has(Number(tgId)));
      return matchesGroup;
    });
  }

  const rowers = filteredRowers;
  const coaches = filteredCoaches;

  const gridPlanningData = {
    ...planningData,
    rowers,
    coaches,
  };

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <div className="flex-shrink-0 border-b border-gray-300 bg-gray-50">
        <div className="flex w-full min-w-0">
          <div
            className="flex flex-shrink-0 items-center border-r border-gray-200 bg-gray-50 p-2 text-sm font-medium text-gray-700"
            style={{ width: `${LABEL_WIDTH}px` }}
          >
            Time
          </div>
          <div className="min-w-0 flex-1 overflow-hidden">
            <div
              className="grid h-full"
              style={{
                gridTemplateColumns,
                minHeight: '40px',
                maxWidth: `${columnWidth * gridCols}px`,
              }}
            >
              {timeSlots.map((time) => (
                <div
                  key={time}
                  className="flex items-center justify-center overflow-hidden border-r border-gray-200 p-1 text-center text-xs text-gray-600 last:border-r-0"
                >
                  <span className="truncate">{time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-auto">
        <div className="border-b border-gray-200">
          <div className="border-b border-gray-200 bg-blue-50 p-2">
            <h3 className="text-sm font-medium text-gray-900">Rowers ({rowers.length})</h3>
          </div>

          <div className="flex w-full min-w-0 border-b border-gray-100">
            <div
              className="flex flex-shrink-0 items-center border-r border-gray-200 bg-gray-50 p-2 text-sm text-gray-700"
              style={{ width: `${LABEL_WIDTH}px` }}
            >
              All Rowers
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <div
                className="grid h-full"
                style={{
                  gridTemplateColumns,
                  minHeight: '48px',
                  maxWidth: `${columnWidth * gridCols}px`,
                }}
              >
                {timeSlots.map((time) => (
                  <TimeSlotCell
                    key={`rowers-${time}`}
                    time={time}
                    planningData={gridPlanningData}
                    onSessionDrop={onSessionDrop}
                    userType="rowers"
                    isResponsive={true}
                  />
                ))}
              </div>
            </div>
          </div>

          {rowers.map((rower) => (
            <div key={rower.id} className="flex w-full min-w-0 border-b border-gray-100">
              <div
                className="flex flex-shrink-0 items-center truncate border-r border-gray-200 p-2 text-xs text-gray-600"
                style={{ width: `${LABEL_WIDTH}px` }}
              >
                {rower.name}
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <div
                  className="grid h-full"
                  style={{
                    gridTemplateColumns,
                    minHeight: '48px',
                    maxWidth: `${columnWidth * gridCols}px`,
                  }}
                >
                  {timeSlots.map((time) => (
                    <TimeSlotCell
                      key={`rower-${rower.id}-${time}`}
                      time={time}
                      planningData={gridPlanningData}
                      onSessionDrop={onSessionDrop}
                      userType="specific"
                      userId={rower.id}
                      isResponsive={true}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-b border-gray-200">
          <div className="border-b border-gray-200 bg-green-50 p-2">
            <h3 className="text-sm font-medium text-gray-900">Coaches ({coaches.length})</h3>
          </div>

          <div className="flex w-full min-w-0 border-b border-gray-100">
            <div
              className="flex flex-shrink-0 items-center border-r border-gray-200 bg-gray-50 p-2 text-sm text-gray-700"
              style={{ width: `${LABEL_WIDTH}px` }}
            >
              All Coaches
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <div
                className="grid h-full"
                style={{
                  gridTemplateColumns,
                  minHeight: '48px',
                  maxWidth: `${columnWidth * gridCols}px`,
                }}
              >
                {timeSlots.map((time) => (
                  <TimeSlotCell
                    key={`coaches-${time}`}
                    time={time}
                    planningData={gridPlanningData}
                    onSessionDrop={onSessionDrop}
                    userType="coaches"
                    isResponsive={true}
                  />
                ))}
              </div>
            </div>
          </div>

          {coaches.map((coach) => (
            <div key={coach.id} className="flex w-full min-w-0 border-b border-gray-100">
              <div
                className="flex flex-shrink-0 items-center truncate border-r border-gray-200 p-2 text-xs text-gray-600"
                style={{ width: `${LABEL_WIDTH}px` }}
              >
                {coach.name}
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <div
                  className="grid h-full"
                  style={{
                    gridTemplateColumns,
                    minHeight: '48px',
                    maxWidth: `${columnWidth * gridCols}px`,
                  }}
                >
                  {timeSlots.map((time) => (
                    <TimeSlotCell
                      key={`coach-${coach.id}-${time}`}
                      time={time}
                      planningData={gridPlanningData}
                      onSessionDrop={onSessionDrop}
                      userType="specific"
                      userId={coach.id}
                      isResponsive={true}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

DailyAvailabilityGrid.propTypes = {
  planningData: PropTypes.shape({
    date: PropTypes.string.isRequired,
    rowers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      })
    ),
    coaches: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        training_group_ids: PropTypes.arrayOf(PropTypes.number),
      })
    ),
    crews: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        rower_ids: PropTypes.arrayOf(PropTypes.number),
      })
    ),
    availability: PropTypes.arrayOf(
      PropTypes.shape({
        user_id: PropTypes.number.isRequired,
        start_time: PropTypes.string.isRequired,
        end_time: PropTypes.string.isRequired,
        availability_type: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  selectedCrews: PropTypes.arrayOf(PropTypes.number),
  onSessionDrop: PropTypes.func.isRequired,
};

export default DailyAvailabilityGrid;
